import * as React from "react";
import {Params, useParams} from "react-router";
import { useCallback, useContext, useEffect } from "react";
import { AppContext, AppContextType } from "../../context/AppContext";
import { Panel } from "../../components/Panel";
import IconComponent from "../../components/IconComponent";
import { getBreadcrumb, setBreadcrumb } from "../../components/Breadcrumb";
import { getDefaultPayload } from "./defaultJson";
import {RouteItem} from "../../routes";
import { ModelPartnerClient } from "@dia-ui/util_interlink-api";

export const TestPage = () => {
  const [inputJSON, setInputJSON] = React.useState<string>("{}");
  const [outputJSON, setOutputJSON] = React.useState<string>("{}");

  const { id, partnerId } = useParams<{ id: string; partnerId?: string; type?: string }>();
  const isPartnerTest = Boolean(partnerId);
  const inputJSONRef = React.useRef<HTMLTextAreaElement>(null);

  const {
    services: {
      serviceClient
    },
    I18N,
    getRoutes
  } = useContext(AppContext);

  const ROUTE_CONFIG = getRoutes();

  /**
   * handleInputChange
   * @param e
   */
  const handleInputChange = (e) => {
    const target = e.detail;
    if (target.value) {
      setInputJSON(target.value);
    }

    e.preventDefault();
  };

  /**
   * handleSend
   * @param _id
   */
  const handleSend = async (_id) => {
    if (!isPartnerTest && !_id) {
      alert(I18N.t("Fehler: Client-ID nicht gefunden."));
      return;
    }

    try {
      const parsedInput = JSON.parse(inputJSON);

      if (!Array.isArray(parsedInput.Product)) {
        throw new Error(I18N.t('Die Eingabe muss ein "Products"-Array enthalten.'));
      }

      const response = isPartnerTest
        ? await serviceClient.testPartner(String(partnerId), parsedInput)
        : await serviceClient.test(String(_id), parsedInput);

      response.data.request = JSON.parse(response.data.request);
      response.data.response = JSON.parse(response.data.response);

      setOutputJSON(JSON.stringify(response.data, null, 2));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : I18N.t("Ein unbekannter Fehler ist aufgetreten.");
      setOutputJSON(`${I18N.t("Fehler")}: ${errorMessage}`);
    }
  };


  const loadClientConfig = useCallback(() => {
    if (id && inputJSONRef && inputJSONRef.current && inputJSON === "{}") {
      const defaultPayload = getDefaultPayload(id),
        defaultPayloadJSON = JSON.stringify(defaultPayload, null, 2);

      setInputJSON(defaultPayloadJSON);

      inputJSONRef.current.innerHTML = defaultPayloadJSON;
      inputJSONRef.current.addEventListener("content-change", (e) => handleInputChange(e));
    }
  }, [id, inputJSON]);

  useEffect(() => {
    loadClientConfig();
  }, [loadClientConfig]);

  return (
    <Panel
      header={
        <>
          <IconComponent iconName={ROUTE_CONFIG.CLIENT_TEST.icon} />
          {ROUTE_CONFIG.CLIENT_TEST.title} - {isPartnerTest ? getBreadcrumb("CLIENT_PARTNER_TEST", "clean") : getBreadcrumb("CLIENT_VIEW_EDIT", "clean")}
        </>
      }
    >
      <dia-form data-form-client-test>
        <dia-grid-nested data-grid-nested-full-width>
          {/* Flex-Container für Input und Output nebeneinander */}
          <dia-grid-row data-grid-column-stretch>
            {/* Input JSON */}
            <dia-grid-column>
              <dia-form-control>
                <label>
                  <strong>{I18N.t("Eingabe")}</strong>
                </label>
                <textarea
                  data-code
                  id="inputJSON"
                  has="code-mirror"
                  ref={ inputJSONRef }
                ></textarea>
              </dia-form-control>
            </dia-grid-column>
            {/* Output JSON */}
            <dia-grid-column>
              <dia-form-control>
                <label>
                  <strong>{I18N.t("Ausgabe")}</strong>
                </label>
                <textarea
                  data-code
                  id="outputJSON"
                  value={outputJSON}
                  readOnly
                  has="code-mirror"
                ></textarea>
              </dia-form-control>
            </dia-grid-column>
          </dia-grid-row>
          <dia-grid-row>
            <dia-grid-column>
              {/* Button darunter */}
              <dia-form-control>
                <button data-primary onClick={() => handleSend(id)}>
                  {I18N.t("Eingabe Senden")}
                </button>
              </dia-form-control>
            </dia-grid-column>
          </dia-grid-row>
        </dia-grid-nested>
      </dia-form>
    </Panel>
  );
};



/**
 * ClientPartnerLoader
 * @param context
 * @param id
 * @param type
 * @constructor
 */
function ClientPartnerLoader(context: AppContextType, id: string, type: string): Promise<ModelPartnerClient> {
  return context.services.servicePartnerClient.getByIdAndType(id, type);
}


/**
 * ClientPartnerTestBreadcrumb
 * @param route
 * @param context
 * @param params
 * @constructor
 */
export function ClientPartnerTestBreadcrumb(route: RouteItem, context: AppContextType, params: Params<string>): string | undefined {
  (async () => {
    if (!params.partnerHash || !params.type) return;

    const partnerClient: ModelPartnerClient = await ClientPartnerLoader(context, params.partnerHash, params.type);
    if (partnerClient && partnerClient[0] && partnerClient[0].partnerId) {
      setBreadcrumb("CLIENT_PARTNER_TEST", partnerClient[0].clientId + ' <-> ' + partnerClient[0].partnerId, "append");
    }
  })();

  return route.title;
}
